/**
 * A node a run has paused at, waiting on someone. Published by the run card
 * that sees the pause and by the canvas beside the chat; the chat's context
 * and composer are this node's until it clears.
 */
export interface PausedNode {
  /** The run, when the chat is following it; resolves the session. */
  runId?: string;
  /** The live session directly, when the canvas reported the pause. */
  sessionId?: string;
  workflowId?: string;
  nodeId: string;
  displayName?: string;
  /** The output under review, when the pause said what it was. */
  reviewContent?: string;
  /**
   * When the node last said it was waiting. It says so again each time it
   * finishes regenerating, and its own chat channel does not, so this is
   * how the chat learns the node is ready for more.
   */
  announcedAt?: number;
  /** Opens the node's output in the canvas beside the chat, when it can. */
  openPreview?: () => void;
}

/** 'new' and the unsaved canvas's draft key 'new:<createdAt>' name no workflow. */
export function savedWorkflowId(id: string | undefined): string | undefined {
  return id && id !== 'new' && !id.startsWith('new:') ? id : undefined;
}

function nonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

/**
 * What the node put up for review, read off a pause event's payload. The
 * backend attaches it as `content` (sometimes under `extra`); older nodes
 * say `message`. One reader for both the run stream and the canvas, so the
 * same pause shows the same thing whichever surface noticed it first.
 */
export function pauseReviewContent(data: unknown): string | undefined {
  if (!data || typeof data !== 'object' || Array.isArray(data))
    return undefined;
  const record = data as Record<string, unknown>;
  const extra =
    record.extra && typeof record.extra === 'object'
      ? (record.extra as Record<string, unknown>)
      : undefined;
  for (const key of ['content', 'message']) {
    const found = nonEmptyString(record[key]) ?? nonEmptyString(extra?.[key]);
    if (found) return found;
  }
  return undefined;
}
